import type { AppConfig } from "../../types/appConfig";
import type { Role } from "../roles/Role.typed";
import type { LoginForm, User, LoginResponse } from "./Auth.typed";
import {
  assertResponseOk,
  expectStatuses,
  fetchZekay,
  formatHttpErrorMessage,
  HttpError,
} from "../zekay/httpClient";

export class AuthModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async login(form: LoginForm): Promise<User | LoginResponse> {
    const response = await fetchZekay(this.zekayAPI + "/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    const text = await response.text();
    let json: Record<string, unknown>;
    try {
      json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      json = {};
    }
    if (response.status !== 200) {
      throw new HttpError({
        status: response.status,
        message: formatHttpErrorMessage(response.status, json, text),
      });
    }
    const payload = json as Record<string, unknown>;
    const rawCompanies = payload.Companies ?? payload.companies;
    if (Array.isArray(rawCompanies)) {
      const rawU = payload.User;
      return {
        ...payload,
        User: rawU && typeof rawU === "object" ? (rawU as User) : (payload.User as User),
        Companies: rawCompanies as LoginResponse["Companies"],
      } as LoginResponse;
    }
    return payload as unknown as User;
  }

  async selectCompany(companyId: number): Promise<User> {
    const response = await fetchZekay(this.zekayAPI + "/auth/select-company", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ company_id: companyId }),
    });
    await assertResponseOk(response);
    const json = (await response.json()) as Record<string, unknown>;
    const rawUser = json.user ?? json.User;
    const rawRole = json.role ?? json.Role;
    const user =
      rawUser && typeof rawUser === "object"
        ? (rawUser as User)
        : ({ id: 0, first_name: "", last_name: "", email: "" } as User);
    const role = rawRole && typeof rawRole === "object" ? (rawRole as Role) : undefined;
    return { ...user, Role: role ?? user.Role ?? undefined };
  }

  async resetRequest(form: LoginForm): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/auth/reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    await expectStatuses(response, 204);
  }

  async resetPassword(form: LoginForm): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/auth/reset/password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    await expectStatuses(response, 200);
  }

  async me(): Promise<LoginResponse> {
    const response = await fetchZekay(this.zekayAPI + "/auth/user");
    await assertResponseOk(response);
    const json = (await response.json()) as Record<string, unknown>;
    const rawU = json.User ?? json.user;
    const rawCompanies = json.Companies ?? json.companies;
    return {
      ...json,
      User: rawU && typeof rawU === "object" ? (rawU as User) : (json.User as User),
      Companies: (Array.isArray(rawCompanies) ? rawCompanies : []) as LoginResponse["Companies"],
    } as LoginResponse;
  }

  async logout(): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    await expectStatuses(response, 204);
  }

  async verifyToken(form: LoginForm): Promise<boolean> {
    const response = await fetchZekay(this.zekayAPI + "/auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    await expectStatuses(response, 204);
    return true;
  }
}
